import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Play, ExternalLink } from 'lucide-react';
import { API } from '../App';

/**
 * Single-slot homepage "Featured Story" — auto-hides when empty.
 * Renders a YouTube embed if video_url is set, else a still image, plus
 * an optional CTA link.
 */
const youTubeEmbed = (url) => {
  if (!url) return null;
  const m = url.match(/(?:youtu\.be\/|v=)([\w-]{6,})/);
  return m ? `https://www.youtube.com/embed/${m[1]}` : null;
};

export default function FeaturedStorySection() {
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/featured-story`)
      .then((r) => setStory(r.data && r.data.is_active ? r.data : null))
      .catch(() => setStory(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;
  if (!story || !story.title) return null;

  const embed = youTubeEmbed(story.video_url);
  const linkHref = story.linked_product_id
    ? `/products/${story.linked_product_id}`
    : story.linked_url;

  return (
    <section className="py-16 md:py-20 bg-gradient-to-br from-amber-50/60 via-background to-amber-50/60" data-testid="featured-story-section">
      <div className="max-w-6xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
        <div className="aspect-video rounded-2xl overflow-hidden shadow-xl bg-foreground/5">
          {embed ? (
            <iframe
              src={embed}
              title={story.title}
              className="w-full h-full"
              allowFullScreen
              loading="lazy"
            />
          ) : story.image_url ? (
            <img src={story.image_url} alt={story.title} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-muted-foreground">
              <Play className="h-16 w-16" />
            </div>
          )}
        </div>
        <div>
          <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-primary bg-primary/10 px-3 py-1.5 rounded-full mb-3">
            Story of the Week
          </span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium text-foreground mb-4" data-testid="featured-story-title">
            {story.title}
          </h2>
          {story.caption && (
            <p className="text-base text-muted-foreground leading-relaxed mb-6">{story.caption}</p>
          )}
          {linkHref && (
            <a
              href={linkHref}
              className="inline-flex items-center gap-2 text-primary hover:underline font-medium"
              data-testid="featured-story-cta"
            >
              See the product
              <ExternalLink className="h-4 w-4" />
            </a>
          )}
        </div>
      </div>
    </section>
  );
}
